/**
 * v3.21-1722 · My Ghost Coach — COMPLETE CONCIERGE PERFORMANCE + REAL HUB DATA + REP RENDER FIXED
 *
 * Private employee dashboard (Meet Your Ghost Coach · Ghost Summary ·
 * My Concierge Portfolio · Client Activity · Phone · Follow-Up · Workflow ·
 * Revenue Protection · Areas of Opportunity · What You're Doing Well ·
 * Client & Revenue at Risk · High-Value Clients Needing Attention ·
 * Longest-Stale · Pipeline Bottlenecks · What Should I Do Today · Goals ·
 * Notes · Trend · Coaching History · Ask Ghost) and Management Team
 * Overview.  Employee sees ONLY their own record; managers see their
 * authorized scope.  Peers never see each other.  No leaderboard.
 *
 * v3.21-1722 RENDER-A/B: Root cause of prior spinner was `const [me,setMe]=useState(null)`
 * with NO fetch — permanent hang meant `EmployeeDashboard` never mounted and zero
 * `/api/ghost-coach/*` requests ever fired.  Fixed by consuming the app-wide
 * `useAuth()` context (which already fetches `/auth/me` on mount) and
 * decoupling the supplemental `/me/capability` request so it can NEVER
 * block the personal dashboard from rendering.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Send, TrendingUp, TrendingDown, AlertTriangle,
         DollarSign, MessageCircle, Target, Camera, Book, Plus,
         User as UserIcon, ShieldCheck, Clock } from 'lucide-react';
import { toast } from 'sonner';
import useDocumentTitle from '../hooks/useDocumentTitle';

const STATUS_COLOR = { green:'bg-emerald-500', yellow:'bg-amber-500', red:'bg-red-500', unknown:'bg-slate-300' };
const STATUS_LABEL = { green:'Above Standard', yellow:'At Standard', red:'Below Standard', unknown:'—' };
const money = n => n == null ? 'Amount Unknown' : `$${Number(n).toLocaleString(undefined,{maximumFractionDigits:0})}`;
const fmt = (v, format) => {
  if (v === null || v === undefined) return '—';
  if (format === 'currency') return money(v);
  if (format === 'percent')  return `${v}%`;
  if (format === 'duration_min') return `${v} min`;
  if (format === 'duration_sec') return `${v} sec`;
  return String(v);
};
const timeAgo = (iso) => {
  if (!iso) return 'never';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m/60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h/24); return `${d}d ago`;
};

function Band({ meta, band }) {
  if (!band) return null;
  const s = band.status || 'unknown';
  const std = band.standard;
  // v3.21-1720 · Zero vs NO DATA vs value.
  const avail = band.availability || 'authoritative';
  const noData = avail === 'no_data' && (band.value === null || band.value === 0 || band.value === undefined);
  return (
    <div className="rounded border p-3 space-y-1 text-xs" data-testid={`band-${meta.key}`}>
      <div className="text-[10px] uppercase text-slate-400">{band.label || meta.label}</div>
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full ${STATUS_COLOR[s]}`} />
        <span className="text-sm font-bold">{noData ? <span className="text-slate-400 italic">NO DATA</span> : fmt(band.value, band.format)}</span>
      </div>
      <div className="text-[10px] text-slate-500">{noData ? 'source not available' : STATUS_LABEL[s]}</div>
      {std && (
        <div className="text-[10px] text-slate-400">
          Std: {std.direction==='higher_is_better'?'≥':'≤'}{fmt(std.green, band.format)}
        </div>
      )}
    </div>
  );
}

const BAND_ORDER = [
  { key: 'daily_successful_contacts',   label: 'Contacts / Day' },
  { key: 'average_call_gap_minutes',    label: 'Call Gap' },
  { key: 'connected_talk_time_seconds', label: 'Talk Time' },
  { key: 'lead_response_minutes',       label: 'Lead Response' },
  { key: 'deals_advanced_per_week',     label: 'Deals Advanced' },
  { key: 'opportunity_at_risk_usd',     label: 'Opp at Risk' },
  { key: 'stale_clients_7d',            label: 'Stale 7+' },
  { key: 'overdue_followups',           label: 'Overdue' },
];

function GoalsSection({ empId, isManagement, onChange }) {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [metric, setMetric] = useState('daily_successful_contacts');
  const [target, setTarget] = useState(8);
  const [reviewDate, setReviewDate] = useState('');
  const [notes, setNotes] = useState('');
  const load = useCallback(async () => {
    try { const r = await api.get(`/ghost-coach/goals/${empId}`); setGoals(r.data.goals || []); }
    catch { setGoals([]); }
  }, [empId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try {
      await api.post('/ghost-coach/goals', {
        employee_id: empId, metric_key: metric,
        target: Number(target), review_date: reviewDate || null,
        notes: notes || null,
      });
      toast.success('Goal created'); setOpen(false); setNotes('');
      load(); onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };
  const updateStatus = async (goalId, status) => {
    try {
      await api.patch(`/ghost-coach/goals/${goalId}/status`, { status });
      toast.success('Goal updated'); load();
    } catch (e) { toast.error('Update failed'); }
  };
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-bold flex items-center gap-1">
          <Target className="h-3.5 w-3.5" /> Current Coaching Goals
        </div>
        {isManagement && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="h-6 text-[10px]" data-testid="new-goal">
                <Plus className="h-3 w-3 mr-1" />New Goal
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Coaching Goal</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <Select value={metric} onValueChange={setMetric}>
                  <SelectTrigger data-testid="goal-metric"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BAND_ORDER.map(b => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" value={target} onChange={e=>setTarget(e.target.value)} placeholder="Target" data-testid="goal-target" />
                <Input type="date" value={reviewDate} onChange={e=>setReviewDate(e.target.value)} data-testid="goal-review" />
                <Textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Notes (optional)" />
                <Button onClick={create} data-testid="goal-save">Save Goal</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {goals.length === 0 ? (
        <div className="text-xs text-slate-400">No active goals yet.</div>
      ) : (
        <ul className="space-y-1 text-xs" data-testid="goals-list">
          {goals.map(g => {
            const met = g.attainment_pct != null && g.attainment_pct >= 100;
            return (
              <li key={g.id} className="border rounded p-2 flex items-start gap-2" data-testid={`goal-${g.id}`}>
                <div className="flex-1">
                  <div className="font-semibold">{g.metric_key.replace(/_/g,' ')}</div>
                  <div className="text-[10px] text-slate-500">
                    Baseline {g.baseline ?? '—'} · Target {g.target} · Current {g.current ?? '—'} · Attainment {g.attainment_pct ?? '—'}%
                  </div>
                  <div className="text-[10px] text-slate-400">Review {g.review_date || 'unscheduled'} · Status: <span className={met?'text-emerald-600':'text-amber-600'}>{g.status}</span></div>
                  {g.notes && <div className="text-[10px] text-slate-600 italic mt-0.5">"{g.notes}"</div>}
                </div>
                {isManagement && g.status === 'active' && (
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => updateStatus(g.id,'met')}>Met</Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => updateStatus(g.id,'not_met')}>Not Met</Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </CardContent></Card>
  );
}

function NotesSection({ empId, isSelf, isManagement }) {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState('employee_visible');
  const [attribution, setAttribution] = useState('ghost_coach');
  const [displayAuthor, setDisplayAuthor] = useState('');
  const load = useCallback(async () => {
    try { const r = await api.get(`/ghost-coach/notes/${empId}`); setNotes(r.data.notes || []); }
    catch { setNotes([]); }
  }, [empId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!text.trim()) return;
    try {
      await api.post('/ghost-coach/notes', {
        employee_id: empId, note: text, visibility, attribution,
        display_author: attribution === 'named_coach' ? displayAuthor : null,
      });
      toast.success('Note saved'); setText(''); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-bold flex items-center gap-1">
          <Book className="h-3.5 w-3.5" /> Coaching Notes
        </div>
        {isManagement && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="h-6 text-[10px]" data-testid="new-note">
                <Plus className="h-3 w-3 mr-1" />New Note
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Coaching Note</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <Textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Coaching note" rows={4} data-testid="note-text" />
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Visibility</div>
                  <Select value={visibility} onValueChange={setVisibility}>
                    <SelectTrigger data-testid="note-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee_visible">Employee Visible</SelectItem>
                      <SelectItem value="management_only">Management Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Display Attribution</div>
                  <Select value={attribution} onValueChange={setAttribution}>
                    <SelectTrigger data-testid="note-attribution"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ghost_coach">Ghost Coach</SelectItem>
                      <SelectItem value="ghost_coach_mgmt_input">Ghost Coach — Management Input</SelectItem>
                      <SelectItem value="named_coach">Named Coach</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {attribution === 'named_coach' && (
                  <Input value={displayAuthor} onChange={e=>setDisplayAuthor(e.target.value)} placeholder="Coach name shown to employee" />
                )}
                <div className="text-[10px] text-slate-500">
                  Immutable audit always preserves the actual author even for Ghost-attributed notes.
                </div>
                <Button onClick={create} data-testid="note-save">Save Note</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {notes.length === 0 ? (
        <div className="text-xs text-slate-400">No notes yet.</div>
      ) : (
        <ul className="space-y-1 text-xs max-h-[240px] overflow-y-auto" data-testid="notes-list">
          {notes.map(n => (
            <li key={n.id} className="border rounded p-2" data-testid={`note-${n.id}`}>
              <div className="flex items-center justify-between">
                <Badge variant={n.visibility === 'management_only' ? 'destructive' : 'outline'}>
                  {n.visibility === 'management_only' ? 'Management Only' : 'Employee Visible'}
                </Badge>
                <span className="text-[10px] text-slate-400">{(n.created_at||'').slice(0,16).replace('T',' ')}</span>
              </div>
              <div className="mt-1">{n.note}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                {n.display_attribution === 'ghost_coach' ? 'Ghost Coach' :
                 n.display_attribution === 'ghost_coach_mgmt_input' ? 'Ghost Coach — Management Input' :
                 (n.display_author ? `Coach: ${n.display_author}` : 'Named Coach')}
                {isManagement && n.author_email && ` · audit: ${n.author_email}`}
              </div>
            </li>
          ))}
        </ul>
      )}
    </CardContent></Card>
  );
}

function EmployeeContextSection() {
  const [entries, setEntries] = useState([]);
  const [text, setText] = useState('');
  const load = async () => {
    try { const r = await api.get('/ghost-coach/my-context'); setEntries(r.data.entries || []); }
    catch { setEntries([]); }
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!text.trim()) return;
    try { await api.post('/ghost-coach/my-context', { context: text }); setText(''); load();
          toast.success('Context saved — measured metrics unchanged.'); }
    catch { toast.error('Save failed'); }
  };
  const remove = async (id) => {
    try { await api.delete(`/ghost-coach/my-context/${id}`); load(); } catch {}
  };
  return (
    <Card><CardContent className="p-4">
      <div className="text-sm font-bold flex items-center gap-1 mb-1">
        <UserIcon className="h-3.5 w-3.5" /> Tell Ghost Your Work Context
      </div>
      <div className="text-[10px] text-slate-500 mb-2">
        Anything Ghost should know about how you work (routines, preferences).
        This never changes your measured metrics or history.
      </div>
      <div className="flex gap-2 mb-2">
        <Input value={text} onChange={e=>setText(e.target.value)} placeholder='e.g. "I spend the first hour reviewing lender responses."' data-testid="ctx-input" />
        <Button size="sm" onClick={add} data-testid="ctx-save">Save</Button>
      </div>
      <ul className="space-y-1 text-xs">
        {entries.map(e => (
          <li key={e.id} className="border rounded p-1 flex items-center gap-2" data-testid={`ctx-${e.id}`}>
            <div className="flex-1">{e.context}</div>
            <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => remove(e.id)}>Remove</Button>
          </li>
        ))}
      </ul>
    </CardContent></Card>
  );
}

function TrendCard({ trend }) {
  if (!trend?.available) {
    return <Card><CardContent className="p-4 text-xs text-slate-500">
      <div className="text-sm font-bold flex items-center gap-1 mb-1">
        <TrendingUp className="h-3.5 w-3.5" /> Performance Trend
      </div>
      No historical snapshots yet.  Use "Freeze Snapshot" above to record today's numbers.
    </CardContent></Card>;
  }
  const rows = [
    { k: 'daily_successful_contacts', label: 'Contacts / Day' },
    { k: 'average_call_gap_minutes',  label: 'Call Gap (min)' },
    { k: 'stale_7d',                   label: 'Stale 7+' },
    { k: 'opportunity_at_risk_usd',   label: 'Opp at Risk ($)' },
    { k: 'deals_advanced_week',       label: 'Deals Adv / Week' },
  ];
  return (
    <Card><CardContent className="p-4">
      <div className="text-sm font-bold flex items-center gap-1 mb-1">
        <TrendingUp className="h-3.5 w-3.5" /> Performance Trend (since {(trend.since||'').slice(0,10)})
      </div>
      <ul className="text-xs space-y-1" data-testid="trend-list">
        {rows.map(r => {
          const d = trend[r.k];
          const icon = d == null ? null : (d > 0 ? <TrendingUp className="inline h-3 w-3 text-emerald-500" /> : <TrendingDown className="inline h-3 w-3 text-red-500" />);
          return <li key={r.k} className="flex justify-between border-b py-0.5">
            <span>{r.label}</span>
            <span className="font-semibold">{d == null ? '—' : (d > 0 ? `+${d}` : d)} {icon}</span>
          </li>;
        })}
      </ul>
    </CardContent></Card>
  );
}

function PerformanceHistorySection({ empId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    setLoading(true);
    try { const r = await api.get(`/ghost-coach/history/${empId}`); setData(r.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  })(); }, [empId]);
  if (loading) return <Card><CardContent className="p-4"><Loader2 className="h-5 w-5 animate-spin" /></CardContent></Card>;
  if (!data) return null;
  return (
    <Card><CardContent className="p-4" data-testid="performance-history">
      <div className="text-sm font-bold mb-2 flex items-center gap-1">
        <Clock className="h-3.5 w-3.5" /> Performance History
      </div>
      {(!data.snapshots || data.snapshots.length === 0) ? (
        <div className="text-xs text-slate-500">No snapshots yet.  Daily scheduled snapshots begin once the worker has run at least once (see backlog).</div>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase text-slate-400 border-b">
              <th className="text-left py-1">Period End</th>
              <th>Kind</th>
              <th>Contacts/day</th>
              <th>Std in Effect</th>
              <th>Call Gap</th>
              <th>Stale 7+</th>
              <th>Opp at Risk</th>
              <th>Deals Adv</th>
            </tr>
          </thead>
          <tbody>
            {data.snapshots.slice(0, 30).map(s => {
              const std = s.standards?.daily_successful_contacts;
              return (
                <tr key={s.id} className="border-b" data-testid={`hist-snap-${s.id}`}>
                  <td className="py-1">{(s.period_end||'').slice(0,10)}</td>
                  <td><Badge variant={s.kind === 'manual_management_snapshot' ? 'destructive' : 'outline'}>{s.kind || '—'}</Badge></td>
                  <td>{s.metrics?.daily_successful_contacts ?? '—'}</td>
                  <td>{std ? `≥${std.green}` : '—'}</td>
                  <td>{s.metrics?.average_call_gap_minutes ?? '—'}m</td>
                  <td>{s.metrics?.stale_7d ?? '—'}</td>
                  <td>{money(s.opportunity_at_risk_usd)}</td>
                  <td>{s.metrics?.deals_advanced_week ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div className="text-[10px] text-slate-500 mt-2">
        Snapshots are immutable — changing today's standard never rewrites prior rows.
      </div>
    </CardContent></Card>
  );
}

function ReportPreviewButton({ empId }) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/ghost-coach/performance-report/${empId}`, { params: { period: '30d' } }); setReport(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };
  const send = async () => {
    if (!recipient.trim()) { toast.error('Recipient email required'); return; }
    if (!window.confirm(`Send this performance report to ${recipient}?\n\nPreview mode — no real email will be dispatched.`)) return;
    setSending(true);
    try {
      const r = await api.post(`/ghost-coach/performance-report/${empId}/send`,
        { employee_id: empId, recipient_email: recipient });
      toast.success(r.data.message || 'Share recorded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={o => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="preview-report">
          <Book className="h-3 w-3 mr-1" />Preview Report
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Employee Performance Report Preview</DialogTitle></DialogHeader>
        {loading || !report ? <div className="p-8 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div> : (
          <div className="space-y-3 text-xs" data-testid="report-preview">
            <div className="border-b pb-2">
              <div className="text-base font-bold">{report.employee.full_name}</div>
              <div className="text-slate-500">{report.employee.role} · Reporting period {report.reporting_period.start.slice(0,10)} → {report.reporting_period.end.slice(0,10)} · {report.reporting_period.working_days} working days</div>
            </div>
            <div>
              <div className="font-semibold mb-1">Above / At / Below Standard</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
                {Object.entries(report.bands).map(([k, b]) => (
                  <div key={k} className="border rounded p-2" data-testid={`report-band-${k}`}>
                    <div className="text-[10px] uppercase text-slate-400">{b.label}</div>
                    <div className="flex items-center gap-1">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${STATUS_COLOR[b.status]}`} />
                      <span className="font-semibold">{fmt(b.value, b.format)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500">{STATUS_LABEL[b.status]}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              <div>
                <div className="font-semibold">Main Areas of Opportunity</div>
                {report.opportunities.length === 0 ? <div className="text-emerald-700">Nothing critical.</div> :
                  <ol className="list-decimal pl-4">{report.opportunities.map((o, i) => <li key={i}>{o.label} — {fmt(o.current, o.format)} vs {'≥'}{fmt(o.standard?.green, o.format)} ({o.status})</li>)}</ol>
                }
              </div>
              <div>
                <div className="font-semibold">What You Are Doing Well</div>
                {report.strengths.length === 0 ? <div className="text-slate-500">—</div> :
                  <ul className="list-disc pl-4">{report.strengths.map((s, i) => <li key={i}>{s.label} at {fmt(s.value, s.format)}</li>)}</ul>
                }
              </div>
            </div>
            <div>
              <div className="font-semibold">Opportunity at Risk</div>
              Total {money(report.opportunity_at_risk.total_usd)} across {report.opportunity_at_risk.unique_deals} Deals.
            </div>
            <div>
              <div className="font-semibold">Coaching Recommendations</div>
              <ul className="list-disc pl-4">{report.coaching_recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
            <div>
              <div className="font-semibold">Employee-Visible Coaching Notes</div>
              {report.employee_visible_notes.length === 0 ? <div className="text-slate-500">No employee-visible notes.</div> :
                <ul className="space-y-1">{report.employee_visible_notes.map((n) => (
                  <li key={n.id} className="border rounded p-1">
                    <div className="text-[10px] text-slate-400">{(n.created_at||'').slice(0,10)} · {n.display_attribution}</div>
                    <div>{n.note}</div>
                  </li>
                ))}</ul>
              }
              <div className="text-[10px] text-red-600 mt-1">Management-only notes are intentionally excluded from this report.</div>
            </div>
            <div>
              <div className="font-semibold">Current Coaching Goals</div>
              {report.current_coaching_goals.length === 0 ? <div className="text-slate-500">No active goals.</div> :
                <ul className="list-disc pl-4">{report.current_coaching_goals.map((g) => (
                  <li key={g.id}>{g.metric_key} · target {g.target} · current {g.current ?? '—'} · {g.status}</li>
                ))}</ul>
              }
            </div>
            {report.progress_since_prior?.available && (
              <div>
                <div className="font-semibold">Progress Since Prior Coaching</div>
                <div>Contacts/day change: {report.progress_since_prior.daily_successful_contacts ?? '—'}</div>
                <div>Call gap change (min): {report.progress_since_prior.average_call_gap_minutes ?? '—'}</div>
                <div>Stale 7+ change: {report.progress_since_prior.stale_7d ?? '—'}</div>
              </div>
            )}
            <div>
              <div className="font-semibold">Team Aggregate Benchmark (anonymized)</div>
              Contacts/day {report.team_benchmark.daily_contacts ?? '—'} · Call Gap {report.team_benchmark.call_gap ?? '—'}m · Stale 7+ {report.team_benchmark.stale_7d ?? '—'}
              <div className="text-[10px] text-slate-500">No coworker names, no rankings.</div>
            </div>
            <div className="text-[10px] text-slate-500 italic border-t pt-2">{report.disclaimer}</div>
            <div className="border-t pt-2">
              <div className="font-semibold mb-1">Send / Share Performance Report</div>
              <div className="flex gap-2 items-center">
                <Input value={recipient} onChange={e=>setRecipient(e.target.value)} placeholder="recipient@company.com" data-testid="report-recipient" />
                <Button size="sm" onClick={send} disabled={sending} data-testid="report-send">
                  {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Send Report'}
                </Button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                Preview mode: sending records an audit trail but does NOT dispatch a real email.
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BaselineBanner({ health, baseline }) {
  // v3.21-1727 · Historical management-verified snapshot reference.
  // Baseline is NEVER current ownership; it is a historical reference
  // shown alongside live-verified current-portfolio metrics.
  if (!baseline) return null;
  const effective = baseline.effective_at ? new Date(baseline.effective_at).toLocaleString() : '—';
  const failed = health === 'live_reconciliation_failed';
  return (
    <Card className={failed ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-slate-50'} data-testid="baseline-banner">
      <CardContent className="p-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-1">
          <ShieldCheck className={`h-3.5 w-3.5 ${failed ? 'text-red-700' : 'text-slate-600'}`} />
          {failed
            ? 'Live Concierge Attribution Unavailable — Showing Historical Verified Baseline'
            : 'Historical Management-Verified Performance Snapshot (Reference Only)'}
        </div>
        <div className="text-xs text-slate-700 space-y-1">
          <div>
            {failed
              ? 'The live Hub client query is currently unavailable. The historical management-verified baseline is shown for reference until live data returns.'
              : 'This is a management-verified historical snapshot. Current-portfolio numbers above come from the live Hub — this baseline is preserved separately for coaching comparison and performance history.'}
          </div>
          {baseline.portfolio_client_count != null && (
            <div className="text-[11px] text-slate-600">
              Baseline: <span className="font-semibold">{baseline.portfolio_client_count} clients</span>
              {baseline.known_portfolio_value != null && <> · <span className="font-semibold">{money(baseline.known_portfolio_value)}</span> known Deal value</>}
              {baseline.amount_unknown_count ? <> · {baseline.amount_unknown_count} Amount Unknown</> : null}
            </div>
          )}
          <div className="text-[11px] text-slate-600">
            Effective: <span className="font-semibold">{effective}</span>
            {baseline.verified_by_name && <> · Verified by <span className="font-semibold">{baseline.verified_by_name}</span></>}
          </div>
          {baseline.source_description && (
            <div className="text-[10px] text-slate-500 italic">{baseline.source_description}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function GhostSummaryCard({ summary, health }) {
  const lines = summary?.lines || [];
  return (
    <Card className="border-slate-300 bg-slate-50" data-testid="ghost-summary">
      <CardContent className="p-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-1">
          <ShieldCheck className="h-3.5 w-3.5" /> Ghost Coach Summary
        </div>
        {lines.length === 0 ? (
          <div className="text-xs text-slate-500 italic">No summary yet.</div>
        ) : (
          <ul className="text-xs space-y-1 leading-snug">
            {lines.map((l, i) => <li key={i} data-testid={`summary-line-${i}`}>{l}</li>)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ConciergePortfolioCard({ metrics }) {
  const ps = metrics?.portfolio_summary || {};
  const items = [
    ['Active Concierge Clients',  ps.active_concierge_clients],
    ['Known Portfolio Value',     ps.known_portfolio_value, 'currency'],
    ['Amount Unknown',            ps.amount_unknown_count, 'count'],
    ['Stale 24h+',                ps.clients_stale_24h],
    ['Stale 3d+',                 ps.clients_stale_3d],
    ['Stale 7d+',                 ps.clients_stale_7d],
    ['Stale 14d+',                ps.clients_stale_14d],
    ['Stale 30d+',                ps.clients_stale_30d],
    ['High-Value Needing Attn',   ps.high_value_clients_needing_attention],
    ['Overdue Follow-Ups',        ps.overdue_followups],
  ];
  return (
    <Card data-testid="concierge-portfolio">
      <CardContent className="p-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-2">
          <UserIcon className="h-3.5 w-3.5" /> My Concierge Portfolio
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {items.map(([label, val, fmt2], i) => (
            <div key={i} className="rounded border p-2 text-xs" data-testid={`portfolio-${label.replace(/[^a-z]/gi,'-').toLowerCase()}`}>
              <div className="text-[10px] uppercase text-slate-400">{label}</div>
              <div className="font-bold">
                {val == null ? <span className="text-slate-400 italic">—</span>
                 : fmt2 === 'currency' ? money(val)
                 : String(val)}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function StaleMoneyBreakdown({ detail }) {
  const rows = [
    { key: '24h', label: '24H+ STALE' },
    { key: '3d',  label: '3+ DAYS STALE' },
    { key: '7d',  label: '7+ DAYS STALE' },
    { key: '14d', label: '14+ DAYS STALE' },
    { key: '30d', label: '30+ DAYS STALE' },
  ];
  return (
    <Card data-testid="stale-money-breakdown">
      <CardContent className="p-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-2">
          <DollarSign className="h-3.5 w-3.5 text-red-500" /> Stale Money Breakdown
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          {rows.map(r => {
            const b = (detail || {})[r.key] || {};
            return (
              <div key={r.key} className="rounded border p-2" data-testid={`stale-bucket-${r.key}`}>
                <div className="text-[10px] uppercase text-slate-400">{r.label}</div>
                <div className="font-bold">{b.client_count ?? 0} Clients</div>
                <div>{money(b.known_deal_value ?? 0)} known</div>
                {b.amount_unknown ? <div className="text-slate-500">{b.amount_unknown} Amount Unknown</div> : null}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function TodayPlanCard({ plan, onBuild, busy }) {
  const picks = plan?.picks || [];
  return (
    <Card className="border-emerald-300 bg-emerald-50" data-testid="today-plan">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-2">
          <Clock className="h-5 w-5 text-emerald-600" />
          <div className="flex-1">
            <div className="text-sm font-bold">What should I do today?</div>
            <div className="text-[10px] text-slate-600">
              Prioritized real-client plan from your stale portfolio, overdue
              follow-ups, and highest-value opportunities.
            </div>
          </div>
          <Button size="sm" onClick={onBuild} disabled={busy}
                  data-testid="what-should-i-do-today">
            {busy ? (<><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Generating…</>)
                   : 'Ask Ghost to build my plan'}
          </Button>
        </div>
        {picks.length === 0 ? (
          <div className="text-xs text-slate-500 italic">
            No prioritized clients yet — Ghost needs authoritative Concierge assignments in Hub.
          </div>
        ) : (
          <ol className="list-decimal pl-5 text-xs space-y-1">
            {picks.map((p, i) => (
              <li key={i} data-testid={`today-pick-${i}`}>
                <span className="font-semibold">{p.client_name || '—'}</span>
                {p.deal_amount != null && <span> · {money(p.deal_amount)}</span>}
                <span className="text-slate-500"> · {p.days_stale}d stale</span>
                {p.deal_stage && <span className="text-slate-500"> · {p.deal_stage}</span>}
                {p.next_action && <span className="text-slate-500"> · Next: {p.next_action}</span>}
                <span className="text-[10px] text-emerald-700"> — {p.reason}</span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function ClientRevenueAtRiskCard({ rows }) {
  const nav = useNavigate();
  const list = rows || [];
  return (
    <Card data-testid="at-risk-table">
      <CardContent className="p-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-2">
          <AlertTriangle className="h-3.5 w-3.5 text-red-500" /> Client &amp; Revenue at Risk
        </div>
        {list.length === 0 ? (
          <div className="text-xs text-slate-500 italic">
            No clients at risk (3+ days stale) in your Concierge portfolio.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase text-slate-400 border-b">
                  <th className="py-1">Client</th>
                  <th>Deal #</th>
                  <th>Deal Amount</th>
                  <th>Stage</th>
                  <th>Next Action</th>
                  <th>Days Stale</th>
                  <th>Follow-Up Due</th>
                </tr>
              </thead>
              <tbody>
                {list.slice(0, 50).map((r, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50 cursor-pointer"
                      data-testid={`at-risk-row-${r.client_id}`}
                      onClick={() => r.client_id && nav(`/clients/${r.client_id}`)}>
                    <td className="py-1 font-semibold">{r.client_name || '—'}</td>
                    <td>{r.deal_number || '—'}</td>
                    <td>{r.deal_amount != null ? money(r.deal_amount) : <span className="text-slate-400 italic">Amount Unknown</span>}</td>
                    <td>{r.deal_stage || '—'}</td>
                    <td>{r.next_action || '—'}</td>
                    <td className={r.days_stale >= 30 ? 'text-red-600 font-bold' : r.days_stale >= 14 ? 'text-red-500' : r.days_stale >= 7 ? 'text-amber-600' : ''}>
                      {r.days_stale}d
                    </td>
                    <td>{r.next_action_due ? (r.next_action_due || '').slice(0,10) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HighValueStaleCard({ rows }) {
  const nav = useNavigate();
  const list = rows || [];
  return (
    <Card data-testid="high-value-stale">
      <CardContent className="p-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-2">
          <DollarSign className="h-3.5 w-3.5 text-emerald-600" /> High-Value Clients Needing Attention
        </div>
        {list.length === 0 ? (
          <div className="text-xs text-slate-500 italic">No high-value stale clients with known Deal Amount.</div>
        ) : (
          <ul className="text-xs space-y-1">
            {list.map((r, i) => (
              <li key={i} className="flex items-center gap-2 border-b py-1 cursor-pointer hover:bg-slate-50"
                  data-testid={`high-value-${r.client_id}`}
                  onClick={() => r.client_id && nav(`/clients/${r.client_id}`)}>
                <div className="flex-1">
                  <div className="font-semibold">{r.client_name}</div>
                  <div className="text-[10px] text-slate-500">
                    {r.days_stale}d stale · {r.deal_stage || '—'} · {r.next_action || 'No Next Action'}
                  </div>
                </div>
                <div className="font-bold">{money(r.deal_amount)}</div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function LongestStaleCard({ rows }) {
  const nav = useNavigate();
  const list = rows || [];
  return (
    <Card data-testid="longest-stale">
      <CardContent className="p-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-2">
          <Clock className="h-3.5 w-3.5 text-red-500" /> Longest-Stale Clients
        </div>
        {list.length === 0 ? (
          <div className="text-xs text-slate-500 italic">No stale clients in your portfolio.</div>
        ) : (
          <ul className="text-xs space-y-1">
            {list.map((r, i) => (
              <li key={i} className="flex items-center gap-2 border-b py-1 cursor-pointer hover:bg-slate-50"
                  data-testid={`longest-${r.client_id}`}
                  onClick={() => r.client_id && nav(`/clients/${r.client_id}`)}>
                <div className="flex-1">
                  <div className="font-semibold">{r.client_name}</div>
                  <div className="text-[10px] text-slate-500">
                    {r.deal_stage || '—'} · {r.next_action || '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-red-600">{r.days_stale}d</div>
                  <div className="text-[10px]">{r.deal_amount != null ? money(r.deal_amount) : 'Amount Unknown'}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PipelineBottleneckCard({ stages }) {
  const list = stages || [];
  return (
    <Card data-testid="pipeline-bottleneck">
      <CardContent className="p-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-2">
          <TrendingDown className="h-3.5 w-3.5 text-amber-500" /> Pipeline Bottlenecks
        </div>
        {list.length === 0 ? (
          <div className="text-xs text-slate-500 italic">
            No stale clients grouped by pipeline stage — either no portfolio or portfolio is fresh.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {list.map((b, i) => (
              <div key={i} className="rounded border p-2 text-xs" data-testid={`bottleneck-${b.stage}`}>
                <div className="font-semibold">{b.stage}</div>
                <div>{b.client_count} clients · {money(b.known_deal_value)} known</div>
                <div className="text-[10px] text-slate-500">Oldest: {b.oldest_days}d stale</div>
                {b.amount_unknown ? <div className="text-[10px] text-slate-500">{b.amount_unknown} Amount Unknown</div> : null}
                {(b.top_clients || []).slice(0, 3).map((c, j) => (
                  <div key={j} className="text-[10px] text-slate-600 truncate">
                    · {c.client_name} · {c.days_stale}d · {c.deal_amount != null ? money(c.deal_amount) : 'Amount Unknown'}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EmployeeDashboard({ empId, viewer, isManagement: isManagementProp }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [chatMsg, setChatMsg] = useState(''); const [chatLog, setChatLog] = useState([]);
  const [chatBusy, setChatBusy] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);
  const chatLogRef = useRef(null);
  const isSelf = viewer && (viewer.user_id === empId || viewer.id === empId);
  // v3.21-1721.2 · Management capability is passed from the parent as
  // a prop (which never blocks the dashboard render).  Fail closed on
  // management access; NEVER block the employee dashboard on the
  // supplemental capability request.
  const isManagement = !!isManagementProp;

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const r = await api.get('/ghost-coach/dashboard',
                                { params: { employee_id: empId, period } });
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load coach';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [empId, period]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!chatMsg.trim()) return;
    const q = chatMsg; setChatMsg(''); setChatBusy(true);
    setChatLog(l => [...l, { role:'user', text:q }]);
    try {
      const r = await api.post('/ghost-coach/chat', { employee_id: empId, message: q });
      setChatLog(l => [...l, { role:'ghost', text: r.data.reply, source: r.data.source }]);
    } catch (e) {
      setChatLog(l => [...l, { role:'ghost', text: 'Chat failed — please try again.' }]);
    }
    finally { setChatBusy(false); }
  };
  const quick = (m) => { setChatMsg(m); setTimeout(() => {
    const btn = document.querySelector('[data-testid="chat-send"]'); btn?.click();
  }, 50); };
  // v3.21-1728 · "Ask Ghost to build my plan" dedicated handler.
  // Uses the SAME chat endpoint with intent=build_today_plan so Ghost
  // consumes the canonical current portfolio (Active + Application
  // Concierge + authoritative Deal Amount).  READY / GENERATING /
  // RESPONSE / ERROR states are visible — never a silent dead click.
  const buildMyPlan = async () => {
    if (planBusy || chatBusy) return;
    setPlanBusy(true);
    const userLine = 'Ask Ghost to build my prioritized plan for today.';
    setChatLog(l => [...l, { role:'user', text: userLine }]);
    // Scroll the chat log into view so the user sees the response.
    setTimeout(() => {
      chatLogRef.current?.scrollIntoView({ behavior:'smooth', block:'start' });
    }, 30);
    try {
      const r = await api.post('/ghost-coach/chat', {
        employee_id: empId,
        message:     userLine,
        intent:      'build_today_plan',
      });
      setChatLog(l => [...l, {
        role: 'ghost', text: r.data.reply, source: r.data.source,
      }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message
                  || 'Ghost could not build your plan right now.';
      setChatLog(l => [...l, {
        role: 'ghost',
        text: `Plan build failed: ${msg}. Try again in a moment.`,
        source: 'error',
      }]);
      toast.error(msg);
    } finally {
      setPlanBusy(false);
      setTimeout(() => {
        chatLogRef.current?.scrollIntoView({ behavior:'smooth', block:'nearest' });
      }, 30);
    }
  };
  const freeze = async () => {
    try { await api.post(`/ghost-coach/snapshot/${empId}`); toast.success('Snapshot frozen'); load(); }
    catch { toast.error('Snapshot failed'); }
  };

  if (loading) return <div className="p-8 text-center" data-testid="coach-loading"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>;
  if (loadError || !data) return (
    <div className="p-6" data-testid="coach-error">
      <Card><CardContent className="p-6 text-center space-y-3">
        <AlertTriangle className="h-6 w-6 mx-auto text-amber-500" />
        <div className="text-sm font-semibold">Ghost Coach couldn't load your performance data.</div>
        <div className="text-xs text-slate-500">{loadError || 'The Ghost Coach service returned no data. Try again.'}</div>
        <Button size="sm" onClick={load} data-testid="coach-retry">Retry</Button>
      </CardContent></Card>
    </div>
  );
  const b = data.bands || {};
  const m = data.metrics || {};

  return (
    <div className="space-y-4" data-testid="my-ghost-coach">
      {/* Meet Your Ghost Coach */}
      <Card><CardContent className="p-4">
        <div className="flex items-center gap-1 text-[10px] uppercase text-slate-400">
          <ShieldCheck className="h-3 w-3" /> Meet Your Ghost Coach
        </div>
        <p className="text-xs text-slate-600 mt-1">
          Ghost is your personal AI performance coach.  Your dashboard is private to you.
          Authorized supervisors and administrators may view your record as part of their
          coaching duties.  Ghost never shares your peers' records with you and never
          shares your record with your peers.
        </p>
      </CardContent></Card>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-sm font-bold" data-testid="employee-name">
          {data.employee?.full_name} · <span className="text-slate-500 font-normal">{data.employee?.role}</span>
        </div>
        <div className="flex-1" />
        {['today','7d','30d','90d'].map(p => (
          <Button key={p} size="sm" variant={period===p?'default':'outline'} className="h-7 text-xs"
                  onClick={() => setPeriod(p)} data-testid={`period-${p}`}>{p}</Button>
        ))}
        {/* v3.21-1719 · Freeze Snapshot is management-only; daily
            scheduled snapshots remain the authoritative source. */}
        {isManagement && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={freeze} data-testid="freeze-snapshot">
            <Camera className="h-3 w-3 mr-1" />Manual Mgmt Snapshot
          </Button>
        )}
        {isManagement && (
          <ReportPreviewButton empId={empId} />
        )}
      </div>

      {/* v3.21-1723 · Management-verified baseline banner (visible when live
          Concierge attribution resolver is broken and baseline is active) */}
      <BaselineBanner health={data.portfolio_data_health} baseline={data.baseline} />

      {/* v3.21-1722 · Ghost Coach Summary (prominent, professional, fact-based) */}
      <GhostSummaryCard summary={data.ghost_summary} health={data.portfolio_data_health} />

      {/* v3.21-1722 · My Concierge Portfolio */}
      <ConciergePortfolioCard metrics={m} health={data.portfolio_data_health} />

      {/* v3.21-1722 · Stale-money buckets */}
      <StaleMoneyBreakdown detail={m.stale_detail} />

      {/* v3.21-1719 · Prominent "What Should I Do Today?" primary CTA */}
      <TodayPlanCard plan={data.today_plan} busy={planBusy} onBuild={buildMyPlan} />

      {/* Personal Ghost Scorecard */}
      <div>
        <div className="text-[10px] uppercase text-slate-400 mb-1">Your Current Performance</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="scorecard">
          {BAND_ORDER.map(meta => <Band key={meta.key} meta={meta} band={b[meta.key]} />)}
        </div>
      </div>

      {/* v3.21-1722 · Client & Revenue at Risk (detailed table) */}
      <ClientRevenueAtRiskCard rows={m.at_risk_rows} />

      {/* v3.21-1722 · High-Value + Longest-Stale + Pipeline Bottleneck */}
      <div className="grid md:grid-cols-2 gap-3">
        <HighValueStaleCard rows={m.high_value_stale} />
        <LongestStaleCard rows={m.longest_stale} />
      </div>
      <PipelineBottleneckCard stages={m.pipeline_bottleneck} />

      {/* Opportunities & Strengths */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-sm font-bold mb-2 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Your Main Areas of Opportunity
          </div>
          {data.opportunities.length === 0 ? (
            <div className="text-xs text-emerald-700">Nothing critical right now — keep it up.</div>
          ) : (
            <ol className="list-decimal pl-5 space-y-1 text-xs">
              {data.opportunities.map((o, i) => (
                <li key={i} data-testid={`opportunity-${i}`}>
                  <span className="font-semibold">{o.label}</span>
                  <span className="text-slate-500">
                    {' '}— Current: {fmt(o.current, o.format)} · Std {o.standard?.direction==='higher_is_better'?'≥':'≤'}{fmt(o.standard?.green, o.format)} · <span className={o.status==='red'?'text-red-600':'text-amber-600'}>{o.status}</span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-sm font-bold mb-2 flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-500" /> What You Are Doing Well
          </div>
          {data.strengths.length === 0 ? (
            <div className="text-xs text-slate-500">Performance improvement should remain the immediate focus for this period.</div>
          ) : (
            <ul className="list-disc pl-5 space-y-1 text-xs" data-testid="strengths-list">
              {data.strengths.map((s, i) => (
                <li key={i}>
                  {s.label} at {fmt(s.value, s.format)} (standard {s.standard?.direction==='higher_is_better'?'≥':'≤'}{fmt(s.standard?.green, s.format)}).
                </li>
              ))}
            </ul>
          )}
        </CardContent></Card>
      </div>

      {/* Opportunity at Risk */}
      <Card><CardContent className="p-4 space-y-2">
        <div className="text-sm font-bold flex items-center gap-1">
          <DollarSign className="h-3.5 w-3.5 text-red-500" /> Opportunity at Risk
        </div>
        <div className="text-xs">Total unique: <b data-testid="total-opp">{money(m.opportunity_at_risk_usd)}</b> across {m.opportunity_at_risk_deals} Deals.</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {Object.entries(m.opportunity_at_risk || {}).map(([k, v]) => (
            <div key={k} className="border rounded p-2" data-testid={`opp-${k}`}>
              <div className="text-[10px] uppercase text-slate-400">{k.replace(/_/g,' ')}</div>
              <div className="font-semibold">{money(v.total)}</div>
              <div className="text-[10px] text-slate-500">{v.deals.length} Deals · {v.amount_unknown} Amount Unknown</div>
              {v.deals.slice(0,3).map((d,i)=>(
                <div key={i} className="text-[10px] text-slate-600 truncate">
                  {d.client_name || '—'} · {d.deal_number || '—'} · {money(d.amount)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </CardContent></Card>

      {/* Trend + Goals side by side */}
      <div className="grid md:grid-cols-2 gap-3">
        <TrendCard trend={data.trend} />
        <GoalsSection empId={empId} isManagement={isManagement} onChange={load} />
      </div>

      {/* Notes */}
      <NotesSection empId={empId} isSelf={isSelf} isManagement={isManagement} />

      {/* v3.21-1719 · Performance History (visible on the dashboard for both
          self and management; every row is immutable). */}
      <PerformanceHistorySection empId={empId} />

      {/* Employee Context (only when viewing your own record) */}
      {isSelf && <EmployeeContextSection />}

      {/* Ask Ghost */}
      <Card><CardContent className="p-4">
        <div className="text-sm font-bold flex items-center gap-1 mb-2">
          <MessageCircle className="h-3.5 w-3.5" /> Ask Your Ghost Coach
        </div>
        <div className="flex gap-1 flex-wrap mb-2">
          {['What should I focus on today?',
            'Why am I below standard?',
            'Which clients should I call first?',
            'How much money is at risk?',
            'How can I improve my contact rate?',
            'Am I improving since my last coaching?',
            'What am I doing well?',
            'Help me plan the rest of my day.'].map(q => (
            <Button key={q} size="sm" variant="outline" className="h-6 text-[10px]"
                    onClick={() => quick(q)} data-testid={`quick-${q.slice(0,10).replace(/[^a-z]/gi,'-')}`}>
              {q}
            </Button>
          ))}
        </div>
        <div ref={chatLogRef} className="border rounded p-2 h-72 overflow-y-auto text-xs space-y-2 bg-slate-50" data-testid="chat-log">
          {chatLog.length === 0 && <div className="text-slate-400 text-center">Ask a question about your own performance…</div>}
          {chatLog.map((mLog, i) => (
            <div key={i} className={mLog.role==='user'?'text-right':'text-left'}>
              <div className={`inline-block max-w-[85%] p-2 rounded ${mLog.role==='user'?'bg-blue-100':'bg-white border'}`}>
                <pre className="whitespace-pre-wrap font-sans text-xs">{mLog.text}</pre>
                {mLog.source && <div className="text-[9px] text-slate-400 mt-1">{mLog.source}</div>}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <Input value={chatMsg} onChange={e=>setChatMsg(e.target.value)} placeholder="What would you like help with?"
                 onKeyDown={e => e.key==='Enter' && !chatBusy && send()}
                 data-testid="chat-input" />
          <Button size="sm" onClick={send} disabled={chatBusy} data-testid="chat-send">
            {chatBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent></Card>
    </div>
  );
}

function TeamOverview() {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const navigate = useNavigate();
  useEffect(() => { (async () => {
    setLoading(true);
    try { const r = await api.get('/ghost-coach/team-overview', { params: { period } }); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load team'); }
    finally { setLoading(false); }
  })(); }, [period]);
  if (loading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>;
  if (!data) return null;
  return (
    <div className="space-y-3" data-testid="team-coach-overview">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-sm font-bold">Team Coach Overview</div>
        <div className="flex-1" />
        {['today','7d','30d','90d'].map(p => (
          <Button key={p} size="sm" variant={period===p?'default':'outline'} className="h-7 text-xs"
                  onClick={()=>setPeriod(p)}>{p}</Button>
        ))}
      </div>
      {data.team_avg && (
        <Card><CardContent className="p-3 text-xs">
          <div className="font-bold mb-1">Team Aggregate Benchmark (anonymized)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>Contacts/day: <b>{data.team_avg.daily_contacts ?? '—'}</b></div>
            <div>Call Gap: <b>{data.team_avg.call_gap ?? '—'}m</b></div>
            <div>Stale 7+: <b>{data.team_avg.stale_7d ?? '—'}</b></div>
            <div>Opp at Risk: <b>{money(data.team_avg.opp_at_risk)}</b></div>
          </div>
        </CardContent></Card>
      )}
      <Card><CardContent className="p-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase text-slate-400 border-b">
              <th className="py-1">Employee</th><th>Role</th>
              <th>Contacts/day</th><th>Calls/day</th><th>Connected</th>
              <th>Call Gap</th><th>Last Call</th><th>Lead Resp</th>
              <th>Overdue</th><th>Stale 7+</th><th>Stale 14+</th>
              <th>Deals Adv</th><th>Opp at Risk</th>
              <th>Areas Needing Attention</th><th></th>
            </tr>
          </thead>
          <tbody>
            {data.employees.map(e => (
              <tr key={e.employee_id} className="border-b hover:bg-slate-50" data-testid={`team-row-${e.employee_id}`}>
                <td className="py-1 font-semibold">{e.full_name}</td>
                <td>{e.role}</td>
                <td>{e.daily_contacts}</td>
                <td>{e.daily_calls}</td>
                <td>{e.daily_connected}</td>
                <td>{e.call_gap ?? '—'}m</td>
                <td className="text-[10px]">{timeAgo(e.last_call)}</td>
                <td>{e.lead_response ?? '—'}m</td>
                <td className={e.overdue_followups > 5 ? 'text-red-600 font-semibold' : ''}>{e.overdue_followups}</td>
                <td className={e.stale_7d > 5 ? 'text-red-600 font-semibold' : ''}>{e.stale_7d}</td>
                <td>{e.stale_14d}</td>
                <td>{e.deals_advanced}</td>
                <td className="font-semibold">{money(e.opp_at_risk)}</td>
                <td className="text-[10px] text-amber-700">{(e.attention || []).join(', ') || '—'}</td>
                <td><Button size="sm" variant="ghost" className="h-6 text-[10px]"
                              onClick={() => navigate(`/growth/my-ghost-coach/${e.employee_id}`)}
                              data-testid={`open-coach-${e.employee_id}`}>Open</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
      <div className="text-[10px] text-slate-500">
        Ghost never recommends any employment action.  Human management makes all
        termination / suspension / demotion / discipline decisions.
      </div>
    </div>
  );
}

export default function MyGhostCoachPage() {
  useDocumentTitle('My Ghost Coach');
  const { employeeId } = useParams();
  // v3.21-1722 · RENDER-F/G · Consume app-wide auth context.
  // AuthContext already fetches /auth/me on mount and exposes user + loading.
  // The previous local `[me, setMe] = useState(null)` had NO populating
  // fetch, which is why the page hung on a spinner and NEVER mounted the
  // EmployeeDashboard, so `/api/ghost-coach/*` requests never fired.
  const { user: me, loading: authLoading, authChecked } = useAuth();
  // v3.21-1722 · Supplemental capability fetch — MUST NEVER block the
  // personal dashboard render.  Fails open (mgmt controls hidden).
  const [canManage, setCanManage] = useState(false);
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    api.get('/ghost-coach/me/capability')
      .then(r => { if (!cancelled) setCanManage(!!r.data?.can_manage_ghost_coach); })
      .catch(() => { if (!cancelled) setCanManage(false); });
    return () => { cancelled = true; };
  }, [me]);
  const isManager = canManage;

  // Auth still initializing OR no user yet — brief indeterminate state.
  if (authLoading && !authChecked) {
    return <div className="p-8 text-center" data-testid="auth-loading">
      <Loader2 className="h-6 w-6 mx-auto animate-spin" />
    </div>;
  }
  if (!me) {
    return <div className="p-8" data-testid="not-authenticated">
      <Card><CardContent className="p-6 text-center">
        <AlertTriangle className="h-6 w-6 mx-auto text-amber-500 mb-2" />
        <div className="text-sm font-semibold">You must be signed in to view Ghost Coach.</div>
      </CardContent></Card>
    </div>;
  }
  if (employeeId) return (
    <div className="p-4" data-build="ghost-coach-v1728">
      <EmployeeDashboard empId={employeeId} viewer={me} isManagement={canManage} />
    </div>
  );
  if (!isManager) return (
    <div className="p-4" data-build="ghost-coach-v1728">
      <EmployeeDashboard empId={me.user_id || me.id} viewer={me} isManagement={canManage} />
    </div>
  );
  return (
    <div className="p-4" data-build="ghost-coach-v1728">
      <Tabs defaultValue="my" className="w-full">
        <TabsList className="mb-3" data-testid="coach-tabs">
          <TabsTrigger value="my">My Coach</TabsTrigger>
          <TabsTrigger value="team">Team Overview</TabsTrigger>
        </TabsList>
        <TabsContent value="my"><EmployeeDashboard empId={me.user_id || me.id} viewer={me} isManagement={canManage} /></TabsContent>
        <TabsContent value="team"><TeamOverview /></TabsContent>
      </Tabs>
    </div>
  );
}
